// Shared so the API, the CMS preview and /play sample one identical curve.
// The fit (peak/sigma from real counts) stays server-side.
import {
  ARENA_TRAFFIC_CURVE_HOURS_PER_DAY,
  ARENA_TRAFFIC_CURVE_MAX_SIGMA_HOURS,
  ARENA_TRAFFIC_CURVE_MIN_SIGMA_HOURS,
} from "@cede/types";

/** Wrapped-normal tails past ±1 day are below float noise, so 3 images suffice. */
const WRAP_IMAGES = [-ARENA_TRAFFIC_CURVE_HOURS_PER_DAY, 0, ARENA_TRAFFIC_CURVE_HOURS_PER_DAY];

/** Shortest bar a non-zero hour draws, so a quiet hour still reads as a bar. Shared: the CMS preview must match /play. */
export const ARENA_TRAFFIC_CURVE_MIN_BAR_PERCENT = 6;

/** Below this circular resultant the day has no peak worth claiming. */
export const ARENA_TRAFFIC_CURVE_FLAT_CONCENTRATION = 0.3;

/** 24 relative weights (index = UTC hour), peak normalized to 1. Wrapped, so a midnight peak keeps both shoulders. */
export const sampleArenaTrafficCurve = ({
  peakHourUtc,
  sigmaHours,
}: {
  peakHourUtc: number;
  sigmaHours: number;
}): number[] => {
  // Out of range there is no curve to draw, only a wrong one: 50 silently
  // aliases onto hour 23, and 1e6 underflows every term so the normalize
  // divides by zero and returns 24 NaNs. Halt instead.
  if (!Number.isFinite(peakHourUtc) || peakHourUtc < 0 || peakHourUtc >= ARENA_TRAFFIC_CURVE_HOURS_PER_DAY) {
    throw new RangeError(`peakHourUtc must be in [0, 24): ${String(peakHourUtc)}`);
  }
  // A non-finite sigma survives the clamp and NaNs every weight.
  if (!Number.isFinite(sigmaHours)) {
    throw new RangeError(`sigmaHours must be finite: ${String(sigmaHours)}`);
  }
  const sigma = Math.min(
    ARENA_TRAFFIC_CURVE_MAX_SIGMA_HOURS,
    Math.max(ARENA_TRAFFIC_CURVE_MIN_SIGMA_HOURS, sigmaHours),
  );
  const weights = Array.from({ length: ARENA_TRAFFIC_CURVE_HOURS_PER_DAY }, (_, hour) =>
    WRAP_IMAGES.reduce((sum, image) => {
      const delta = hour - peakHourUtc + image;
      return sum + Math.exp(-(delta * delta) / (2 * sigma * sigma));
    }, 0),
  );

  const max = Math.max(...weights);
  return weights.map((weight) => weight / max);
};

/** Whole-hour peak of a sampled curve. */
export const arenaTrafficCurvePeakHour = (hourlyWeights: readonly number[]): number =>
  hourlyWeights.indexOf(Math.max(...hourlyWeights));
